"use client"

import { useState, useEffect } from "react"
import { TrashIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Sidebar from "../components/Sidebar.js"
import { getOrders } from "../comman/Orders.js"

const OrderTable = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const orders = await getOrders()
        setData(orders)
      } catch (err) {
        setError("Error fetching orders. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [])

  const handleDelete = (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      setData((prevData) => prevData.filter((item) => item._id !== orderId))
    }
  }

  const filteredOrders = data.filter(
    (order) =>
      order.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.coin.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customerId.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customerAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customerMobileNo.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Order Management</h1>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                  placeholder="Search orders"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto -mx-4 md:-mx-6">
              <div className="inline-block min-w-full align-middle p-4 md:p-6">
                <div className="overflow-hidden border rounded-lg">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-[#12143F]">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Sr. No
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Order ID
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Product Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Coin
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Customer ID
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Customer Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Customer Address
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Customer Mobile No
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredOrders.map((row, index) => (
                          <tr key={row._id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{index + 1}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{row.orderId}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                              {row.productName}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{row.coin}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{row.customerId}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[150px] truncate">
                              {row.customerName}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                              {row.customerAddress}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                              {row.customerMobileNo}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleDelete(row._id)}
                                className="text-red-500 hover:text-red-700 transition-colors duration-200"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderTable

